import workflow from '@banking/ui/lib/workflow';
import { MAX_BITCOIN_WITHHOLDING_BPS } from '@banking/ui/utils/constants';
import { ActionType, handleCreateInvestmentAccountRequestAction, handleCreateInvestmentAccountResponseAction, handleCreateBtcWithholdingConfigRequestAction, handleCreateBtcWithholdingConfigResponseAction, handleGetCurrentBitcoinPriceRequestAction, handleGetCurrentBitcoinPriceResponseAction, handleGetMaxWithholdingRequestAction, handleGetMaxWithholdingResponseAction, handleListInvestmentAccountsRequestAction, handleListInvestmentAccountsResponseAction, handleListRecentActivityRequestAction, handleListRecentActivityResponseAction, handleNavigateToBankingHomeAction, handleRetrieveInvestEligibilityRequestAction, handleRetrieveInvestEligibilityResponseAction, handleRotateDisplayMode, handleSetOnboardingScreenAction, handleSetTwoFactorAuthEnabledAction, handleUpdateBtcWithholdingConfigRequestAction, handleUpdateBtcWithholdingConfigResponseAction, handleToggleModalOpenAction, handleSetGrantAction, handleSetWaitlistedAction, handleAddToRiskWaitlistAction } from './actions';
import { handleUserChangePhoneNumber, handlePhoneNumberEnrollTwoFactorError, handlePhoneNumberEnrollTwoFactorReceived, handleToggleButtonLoading, handleUserChangeVerificationCode, handleUserClickEnroll, handleUserClickResendCode, handleUserClickVerify, handleVerifyCodeEnrollTwoFactorError, handleVerifyCodeEnrollTwoFactorReceived } from './two-factor';
import { get as getFromLocalStorage } from '@square/dashboard-utilities/utils/persistable';
export * from './actions';
export * from './selectors';
export * from './two-factor';

// State
// =============================================================================

export let DisplayMode = /*#__PURE__*/function (DisplayMode) {
  DisplayMode[DisplayMode["BTC_MODE"] = 0] = "BTC_MODE";
  DisplayMode[DisplayMode["SATOSHI_MODE"] = 1] = "SATOSHI_MODE";
  DisplayMode[DisplayMode["FIAT_MODE"] = 2] = "FIAT_MODE";
  return DisplayMode;
}({});
export let EntityType = /*#__PURE__*/function (EntityType) {
  EntityType["SOLE_PROPRIETORSHIP"] = "SOLE_PROPRIETORSHIP";
  EntityType["SINGLE_MEMBER_LLC"] = "SINGLE_MEMBER_LLC";
  EntityType["LLC"] = "LLC";
  EntityType["PRIVATE_COMPANY"] = "PRIVATE_COMPANY";
  EntityType["PUBLIC_COMPANY"] = "PUBLIC_COMPANY";
  EntityType["NONPROFIT"] = "NONPROFIT";
  EntityType["PARTNERSHIP"] = "PARTNERSHIP";
  return EntityType;
}({});

// Set when seller chooses an unsupported business type
export const IS_IN_BTC_BUSINESS_TYPE_WAITLIST_LOCAL_STORAGE_KEY = 'Invest.isInBitcoinConversionsWaitlist';

// Set when retrieveInvestEligibility returns false and seller starts the onboarding flow
export const IS_IN_BTC_RISK_WAITLIST_LOCAL_STORAGE_KEY = 'Invest.isInBtcRiskWaitlist';
export let FetchState = /*#__PURE__*/function (FetchState) {
  FetchState[FetchState["NOT_REQUESTED"] = 0] = "NOT_REQUESTED";
  FetchState[FetchState["LOADING"] = 1] = "LOADING";
  FetchState[FetchState["COMPLETED"] = 2] = "COMPLETED";
  FetchState[FetchState["ERROR"] = 3] = "ERROR";
  return FetchState;
}({});
export let RiskEligibility = /*#__PURE__*/function (RiskEligibility) {
  RiskEligibility[RiskEligibility["NOT_REQUESTED"] = 0] = "NOT_REQUESTED";
  RiskEligibility[RiskEligibility["LOADING"] = 1] = "LOADING";
  RiskEligibility[RiskEligibility["ELIGIBLE"] = 2] = "ELIGIBLE";
  RiskEligibility[RiskEligibility["INELIGIBLE"] = 3] = "INELIGIBLE";
  RiskEligibility[RiskEligibility["ERROR"] = 4] = "ERROR";
  return RiskEligibility;
}({});
export const INITIAL_STATE = {
  accounts: [],
  activities: [],
  btcAccount: undefined,
  canSeeBitcoinFiatConversion: true,
  canSeeCashTagLinking: false,
  cashtag: '',
  currentBitcoinPrice: undefined,
  currentOnboardingScreen: 'splash',
  displayMode: DisplayMode.FIAT_MODE,
  getCurrentBitcoinPriceState: FetchState.NOT_REQUESTED,
  grantId: '',
  listInvestmentAccountState: FetchState.NOT_REQUESTED,
  isLoading: false,
  isLoadingScreen: 'linkingSuccess',
  isInBusinessTypeWaitlist: getFromLocalStorage(IS_IN_BTC_BUSINESS_TYPE_WAITLIST_LOCAL_STORAGE_KEY) || false,
  isInRiskWaitlist: getFromLocalStorage(IS_IN_BTC_RISK_WAITLIST_LOCAL_STORAGE_KEY) || false,
  isTwoFactorEnabled: false,
  maxWithholdingBps: MAX_BITCOIN_WITHHOLDING_BPS,
  phoneNumber: '',
  phoneNumberError: '',
  riskEligibility: RiskEligibility.NOT_REQUESTED,
  showToggleToast: false,
  toggleButtonLoading: false,
  verificationCode: '',
  verificationCodeError: '',
  withholdingBps: 0,
  withholdingConfig: undefined,
  isModalOpen: false
};
export const createState = (overrides = {}) => ({
  ...INITIAL_STATE,
  ...overrides
});
export const update = context =>
// eslint-disable-next-line complexity
(state, action) => {
  switch (action.type) {
    case ActionType.AddToRiskWaitlist:
      return handleAddToRiskWaitlistAction(context, state, action);
    case ActionType.CreateInvestmentAccountRequest:
      return handleCreateInvestmentAccountRequestAction(context, state, action);
    case ActionType.CreateInvestmentAccountResponse:
      return handleCreateInvestmentAccountResponseAction(context, state, action);
    case ActionType.CreateBtcWithholdingConfigRequest:
      return handleCreateBtcWithholdingConfigRequestAction(context, state, action);
    case ActionType.CreateBtcWithholdingConfigResponse:
      return handleCreateBtcWithholdingConfigResponseAction(context, state, action);
    case ActionType.GetCurrentBitcoinPriceRequest:
      return handleGetCurrentBitcoinPriceRequestAction(context, state, action);
    case ActionType.GetCurrentBitcoinPriceResponse:
      return handleGetCurrentBitcoinPriceResponseAction(context, state, action);
    case ActionType.GetMaxWithholdingRequest:
      return handleGetMaxWithholdingRequestAction(context, state, action);
    case ActionType.GetMaxWithholdingResponse:
      return handleGetMaxWithholdingResponseAction(context, state, action);
    case ActionType.ListInvestmentAccountsRequest:
      return handleListInvestmentAccountsRequestAction(context, state, action);
    case ActionType.ListInvestmentAccountsResponse:
      return handleListInvestmentAccountsResponseAction(context, state, action);
    case ActionType.ListRecentActivityRequest:
      return handleListRecentActivityRequestAction(context, state, action);
    case ActionType.ListRecentActivityResponse:
      return handleListRecentActivityResponseAction(context, state, action);
    case ActionType.NavigateToBankingHome:
      return handleNavigateToBankingHomeAction(context, state, action);
    case ActionType.RetrieveInvestEligibilityRequest:
      return handleRetrieveInvestEligibilityRequestAction(context, state, action);
    case ActionType.RetrieveInvestEligibilityResponse:
      return handleRetrieveInvestEligibilityResponseAction(context, state, action);
    case ActionType.RotateDisplayMode:
      return handleRotateDisplayMode(context, state, action);
    case ActionType.SetGrant:
      return handleSetGrantAction(context, state, action);
    case ActionType.SetOnboardingScreen:
      return handleSetOnboardingScreenAction(context, state, action);
    case ActionType.SetTwoFactorAuthEnabled:
      return handleSetTwoFactorAuthEnabledAction(context, state, action);
    case ActionType.SetWaitlisted:
      return handleSetWaitlistedAction(context, state, action);
    case ActionType.UpdateBtcWithholdingConfigRequest:
      return handleUpdateBtcWithholdingConfigRequestAction(context, state, action);
    case ActionType.UpdateBtcWithholdingConfigResponse:
      return handleUpdateBtcWithholdingConfigResponseAction(context, state, action);
    case ActionType.ToggleModalOpen:
      return handleToggleModalOpenAction(context, state, action);
    case ActionType.UserChangePhoneNumber:
      return handleUserChangePhoneNumber(state, action);
    case ActionType.UserChangeVerificationCode:
      return handleUserChangeVerificationCode(state, action);
    case ActionType.UserClickEnroll:
      return handleUserClickEnroll(state, context);
    case ActionType.UserClickVerify:
      return handleUserClickVerify(state, context);
    case ActionType.UserClickResendCode:
      return handleUserClickResendCode(state, context);
    case ActionType.PhoneNumberEnrollTwoFactorReceived:
      return handlePhoneNumberEnrollTwoFactorReceived(state, action, context);
    case ActionType.PhoneNumberEnrollTwoFactorError:
      return handlePhoneNumberEnrollTwoFactorError(state, context);
    case ActionType.VerifyCodeEnrollTwoFactorReceived:
      return handleVerifyCodeEnrollTwoFactorReceived(state, action, context);
    case ActionType.VerifyCodeEnrollTwoFactorError:
      return handleVerifyCodeEnrollTwoFactorError(state, context);
    case ActionType.ToggleButtonLoading:
      return handleToggleButtonLoading(state, action);
  }
};
const investments = (seed, context) => {
  const w = workflow(update(context), seed);
  return {
    states: w.states,
    updates: w.updates,
    emit: w.emit
  };
};
export default investments;